import CloseIcon from "@mui/icons-material/Close";
import Skeleton from "@mui/material/Skeleton";
import { styled } from "@mui/material/styles";
import NodeDataStore from "csi/datastore/NodeDataStore";
import { CHANGE_TRACKING } from "csi/service/rightPaneService";
import WorkingCache from "csi/state/WorkingCache";
import { rightPaneStore, routerStore } from "csi/state/rootStore";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import Search from "csi/xml/Search";
import FlexColumn from "designSystem/components/flex/FlexColumn";
import IconArrowExpandHorizontal from "designSystem/components/icon/ArrowExpandHorizontal";
import PaneHeader from "designSystem/components/pane/PaneHeader";
import { useLocalStore } from "designSystem/hooks";
import { Box, DataGrid, GalleryPlaceholder, IconButton } from "designSystem/index";
import { grey } from "designSystem/styles/color";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import { action, makeObservable, observable } from "mobx";
import { observer } from "mobx-react-lite";
import React, { createContext, useContext } from "react";
import { ChangeLogCard } from "./ChangeLogCard";
import getFilterData from "./ChangeLogSlimFilter";
import MultiFilter, { FilterParams } from "./component/MultiFilter";

const PREFIX = "ChangeLogSlimOnInstance";

const classes = {
	placeholder: `${PREFIX}placeholder`,
	filterSeparator: `${PREFIX}filterSeparator`,
};

const StyledFlexColumn = styled(FlexColumn)((
	{
		theme: Theme,
	},
) => ({
	[`& .${classes.placeholder}`]: {
		"& img": {
			width: 270,
		},
	},

	[`& .${classes.filterSeparator}`]: {
		borderBottomWidth: 1,
		borderBottomStyle: "solid",
		borderBottomColor: grey[400],
		height: 6,
	},
}));

const LOG_ENTRY = "ChangeLogEntry";
const CONTEXT_INSTANCE_CNL = "ContextInstanceCnl";

class ChangeLogNodeStore{
	@observable.ref private store: NodeDataStore;	
	private filterData: FilterParams;
	private query: Query;
	private cache: WorkingCache;

	constructor() {
		makeObservable(this);
		// create filter data
		const { locationType }  = routerStore;
		this.filterData = getFilterData(locationType);

		this.cache = new WorkingCache();

		this.updateFilterData();
	}
	/** handler to clean up - not used yet but must present*/
	onCleanup?: (instance: ChangeLogNodeStore) => void;

	@action public updateFilterData = ()=>{
		// create node store
		const search = new Search({ Module: "ChangeTracking", Operation: "ChangeLogCNLsQueryByXML" });
		search.customOperationSupportsPaging = true;
		this.store = new NodeDataStore(search, this.cache);

		// update filter data
		const { locationUrl }  = routerStore;
		this.query = search.getQuery()
			.Type(LOG_ENTRY)
			.RefAttr(CONTEXT_INSTANCE_CNL, xml.OP_EQUALS, locationUrl);
		this.filterData.filterData.forEach((item) => {
			item.queryFilter?.(this.query);
		});
	};

	get dataStore(): NodeDataStore {
		return this.store;
	}

	get multiFilterData() {
		return this.filterData;
	}
}

const ChangeLogNodeStoreContext = createContext<ChangeLogNodeStore>(undefined);
const useChangeLogNodeStore = () => useContext(ChangeLogNodeStoreContext);

export const ChangeLogSlimOnInstance = () => {
	const nodeStore = useLocalStore(()=>new ChangeLogNodeStore());
	return (
		<>
			{nodeStore && (
				<ChangeLogNodeStoreContext.Provider value={nodeStore}>
					<PrivateChangeLogSlimOnInstance />
				</ChangeLogNodeStoreContext.Provider>
			)}
		</>
	);
};

const PrivateChangeLogSlimOnInstance = observer(() => {
	const SkeletonCard = () => (
		<Box
			bgcolor="background.paper"
			border={STANDARD_BORDER_STYLE}
			height={1}
			p={2}
		>
			<Skeleton height={100} variant="rectangular" />
		</Box>
	);

	const PlaceholderComponent = () => (
		<GalleryPlaceholder className={classes.placeholder} titleProps={{ res: "ChangeLogNoChanges" }} />
	);

	const onClose = () => {
		rightPaneStore.closeSelected();
	};

	const onOpenDetailedView = () => {
		rightPaneStore.setSelected(CHANGE_TRACKING);
	};

	const {
		dataStore,
		multiFilterData,
		updateFilterData,
	} = useChangeLogNodeStore();

	const onFilterChange = () => {
		updateFilterData();
	};

	return (
		<StyledFlexColumn>
			<PaneHeader titleRes="LogChangeLog">
				<IconButton
					design="default"
					onClick={onOpenDetailedView}
					res="ChangeLogDetailed"
					size="large"
				>
					<IconArrowExpandHorizontal />
				</IconButton>
				<IconButton
					design="default"
					onClick={onClose}
					res="BreadcrumbLogClose"
					size="large"
				>
					<CloseIcon />
				</IconButton>
			</PaneHeader>
			<Box flexGrow={1} overflow="auto">
				<MultiFilter filterParams={multiFilterData} onChange={onFilterChange} />
				<Box className={classes.filterSeparator} />
				<DataGrid
					CardComponent={ChangeLogCard}
					PlaceholderComponent={PlaceholderComponent}
					SkeletonComponent={SkeletonCard}
					columns={1}
					infiniteScrolling
					rowsPerPage={50}
					store={dataStore}
				/>
			</Box>
		</StyledFlexColumn>
	);
});

export default ChangeLogSlimOnInstance;